import { useEffect, useRef, useState } from 'react';
import { TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { SEARCH_DEBOUNCE_MS } from '@/lib/useListUrlState';

/**
 * A list's search box. Keeps its own text (so typing doesn't re-render the list) and reports
 * it once typing pauses. Follows `value` when it changes from outside (back/forward, a link).
 */
export function ListSearch({
  value,
  onSearch,
  label,
  placeholder,
  w = 240,
}: {
  value: string | undefined;
  onSearch: (q: string) => void;
  label: string;
  placeholder: string;
  w?: number;
}) {
  const current = value ?? '';
  const [text, setText] = useState(current);
  const [debounced] = useDebouncedValue(text.trim(), SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setText((t) => (t.trim() === current ? t : current));
  }, [current]);

  // Report only when the settled text itself changes, so going back isn't overwritten by stale
  // input.
  const last = useRef(debounced);
  useEffect(() => {
    if (last.current === debounced) return;
    last.current = debounced;
    if (debounced !== current) onSearch(debounced);
  }, [debounced, current, onSearch]);

  return (
    <TextInput
      leftSection={<IconSearch size={16} />}
      placeholder={placeholder}
      aria-label={label}
      value={text}
      onChange={(e) => setText(e.currentTarget.value)}
      w={w}
    />
  );
}
